<template>
  <div v-if="currentStage" class="progress-stepper">
    <div class="progress-stepper__pill">
      <div
        v-for="(stage, index) in stages"
        :key="stage"
        class="progress-stepper__item"
        :class="{
          'progress-stepper__item--completed': index < currentIndex,
          'progress-stepper__item--current': index === currentIndex,
        }"
      >
        <span class="progress-stepper__icon">
          <!-- Completed: green circle with checkmark -->
          <svg v-if="index < currentIndex" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#22C55E" stroke="#E5E7EB" />
            <path d="M17.281 7.97c.293.293.293.769 0 1.062l-6 6a.75.75 0 0 1-1.062 0l-3-3a.75.75 0 1 1 1.062-1.062l2.47 2.468 5.47-5.468a.75.75 0 0 1 1.063 0z" fill="#fff" />
          </svg>
          <!-- Current: blue circle with step number -->
          <span v-else-if="index === currentIndex" class="progress-stepper__number progress-stepper__number--current">
            {{ index + 1 }}
          </span>
          <!-- Future: gray circle with step number -->
          <span v-else class="progress-stepper__number">
            {{ index + 1 }}
          </span>
        </span>
        <span class="progress-stepper__label">{{ stage }}</span>
        <svg v-if="index < stages.length - 1" class="progress-stepper__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.2 12L9.3 15.9a1.1 1.1 0 0 0 0 1.4c.2.2.4.3.7.3.3 0 .5-.1.7-.3l4.6-4.6c.1-.1.2-.2.2-.3.1-.1.1-.3.1-.4 0-.1 0-.3-.1-.4 0-.1-.1-.2-.2-.3l-4.6-4.6a1 1 0 0 0-.7-.3 1 1 0 0 0-.7.3 1.1 1.1 0 0 0 0 1.4L13.2 12z" fill="#1E293B" />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
const STAGES = [
  'Create Campaign Details',
  'Create Brief',
  'Create Group',
  'Create Campaign Draft',
]

// Node → stage mapping
// "Create Brief" starts at ask_constraints (after cover image is finalized)
// "Create Group" starts at check_country_industry (after brief is accepted)
const STEP_TO_STAGE = {
  // Stage 1: Create Campaign Details
  greeting: 'Create Campaign Details',
  extract_info: 'Create Campaign Details',
  check_brand: 'Create Campaign Details',
  ask_brand: 'Create Campaign Details',
  check_details: 'Create Campaign Details',
  ask_details: 'Create Campaign Details',
  generate_campaign: 'Create Campaign Details',
  check_confirmation: 'Create Campaign Details',
  user_wants_changes: 'Create Campaign Details',

  // Stage 1 continued: cover image is part of campaign details
  ask_image_choice: 'Create Campaign Details',
  check_image_choice: 'Create Campaign Details',
  ask_image_details: 'Create Campaign Details',
  process_image_details: 'Create Campaign Details',
  ask_more_image_details: 'Create Campaign Details',
  user_wants_to_upload: 'Create Campaign Details',
  needs_more_image_details: 'Create Campaign Details',
  user_wants_new_image_style: 'Create Campaign Details',
  ask_image_refinement: 'Create Campaign Details',
  process_image_refinement: 'Create Campaign Details',
  user_wants_image_refinement: 'Create Campaign Details',
  needs_refinement_details: 'Create Campaign Details',
  generate_cover: 'Create Campaign Details',
  confirm_cover: 'Create Campaign Details',
  check_cover_response: 'Create Campaign Details',
  user_wants_cover_regeneration: 'Create Campaign Details',

  // Stage 2: Create Brief (starts at constraints, after cover image is done)
  ask_constraints: 'Create Brief',
  process_constraints: 'Create Brief',
  suggest_deliverables: 'Create Brief',
  check_deliverables_response: 'Create Brief',
  has_confirmed_deliverables: 'Create Brief',
  generate_brief: 'Create Brief',
  check_brief_response: 'Create Brief',
  user_wants_brief_changes: 'Create Brief',

  // Stage 3: Create Group
  check_country_industry: 'Create Group',
  ask_country_industry: 'Create Group',
  extract_country_industry: 'Create Group',
  suggest_niches: 'Create Group',
  check_niches_response: 'Create Group',
  has_confirmed_niches: 'Create Group',
  suggest_sizes: 'Create Group',
  check_sizes_response: 'Create Group',
  has_confirmed_sizes: 'Create Group',
  recommend_specs: 'Create Group',
  needs_country_or_industry: 'Create Group',

  // Stage 4: Create Campaign Draft
  request_entity_creation: 'Create Campaign Draft',
  check_visibility: 'Create Campaign Draft',
  recommend_influencers: 'Create Campaign Draft',
  process_selection: 'Create Campaign Draft',
  finalize: 'Create Campaign Draft',
  is_private_campaign: 'Create Campaign Draft',
}

export function getStageFromStep(step) {
  return STEP_TO_STAGE[step] || null
}

export default {
  name: 'ProgressStepper',

  props: {
    currentStage: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      stages: STAGES,
    }
  },

  computed: {
    currentIndex() {
      if (this.currentStage === '__completed__') return this.stages.length
      const idx = STAGES.indexOf(this.currentStage)
      return idx >= 0 ? idx : 0
    },
  },
}
</script>

<style lang="scss" scoped>
.progress-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;

  &__pill {
    display: inline-flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 8px 20px;
    gap: 0;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    background: #e5e7eb;
    color: #6b7280;

    &--current {
      background: #3b82f6;
      color: #ffffff;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #9ca3af;
  }

  &__item--completed &__label {
    color: #22C55E;
  }

  &__item--current &__label {
    color: #1E293B;
    font-weight: 600;
  }

  &__chevron {
    margin: 0 8px;
    flex-shrink: 0;
  }
}
</style>
